import "../arac-takip/src/components/Navbar.css";
import React, { useEffect, useMemo, useState } from "react";
import { collection, doc, getDocs, setDoc, updateDoc } from "firebase/firestore";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { createNewUser, db } from "../firebaseConfig";
import { useAuth } from "../context/AuthContext";

const normalizeRole = (role = "") => String(role).toLocaleLowerCase("tr-TR");
const isCreatorRole = (role) => ["oluşturucu", "olusturucu", "creator"].includes(normalizeRole(role));
const canManagePermissions = (role, permissions = {}) =>
  isCreatorRole(role) || permissions?.yetkiKontrol === true || permissions?.YetkiKontrol === true;

const MODULES = [
  {
    key: "arac-takip",
    title: "Araç Takip",
    fields: [
      ["giris", "Eksiklik ekleyebilir."],
      ["takip", "Eksiklikleri ve kayıtları görüntüleyebilir."],
      ["cozulenler", "Çözülen eksiklikleri görüntüleyebilir."],
      ["istatistik", "İstatistik sayfasını görüntüleyebilir."],
      ["termin", "Termin ataması yapabilir."],
      ["tehdit", "Tehdit listesini görüntüleyebilir."],
      ["loglar", "Giriş loglarını görüntüleyebilir."],
      ["yonetici", "Araç takipte tüm yetkilere sahip olur."],
    ],
  },
  {
    key: "kontrol-formlari",
    title: "Kontrol Formları",
    fields: [
      ["doldurur", "Form doldurabilir."],
      ["olusturur", "Form şablonu oluşturabilir ve düzenleyebilir."],
      ["gorur", "Kontrol eksiklikleri raporunu görüntüleyebilir."],
      ["cozer", "Tüm kontrol eksikliklerini çözüldü işaretleyebilir."],
      ["sadeceKendiEksikleriniCozer", "Sadece kendi oluşturduğu kontrol eksikliklerini çözebilir."],
      ["gecmisTarihDoldurur", "Bugün dışında son 2 güne ait form doldurabilir."],
      ["yeniHedefYazar", "Form doldururken listede olmayan yeni şantiye veya tesis adı yazabilir."],
      ["mailAyarlar", "Kontrol formları için otomatik mail şablonu ve gönderim görevi oluşturabilir."],
      ["yonetici", "Kontrol formlarında tüm yetkilere sahip olur."],
    ],
    plates: true,
  },
  {
    key: "fatura",
    title: "Fatura Takip",
    fields: [
      ["takip", "Fatura giriş, liste ve rapor sayfalarını kullanabilir."],
      ["yonetici", "Excel yükleme ve yönetici işlemlerini yapabilir."],
    ],
  },
  {
    key: "kazatakip",
    title: "Kaza Takip",
    fields: [
      ["giris", "Kaza modülüne giriş yapabilir ve kayıt oluşturabilir."],
      ["takip", "Kaza kayıtlarını görüntüleyebilir."],
      ["yonetici", "Kaza modülünde yönetici işlemlerini yapabilir."],
    ],
  },
  {
    key: "personeltakip",
    title: "Personel Takip",
    fields: [
      ["giris", "Personel kayıt ve belge giriş işlemlerini yapabilir."],
      ["takip", "Personel listesi ve raporlarını görüntüleyebilir."],
      ["yonetici", "Personel modülünde yönetici işlemlerini yapabilir."],
    ],
  },
  {
    key: "mazot-takip",
    title: "Mazot & Tank Takip",
    fields: [
      ["giren", "Dolum ve yakıt girişi yapabilir."],
      ["giren_onaylayan", "Giriş yapabilir ve onay/tank işlemlerini kullanabilir."],
      ["izleyen", "Rapor ve hareketleri görüntüleyebilir."],
      ["olusturucu", "Mazot modülünde tüm yetkilere sahip olur."],
    ],
  },
  {
    key: "hazir-beton",
    title: "Hazır Beton",
    fields: [
      ["giris", "Hazır beton operasyon girişi yapabilir."],
      ["rapor", "Hazır beton raporlarını görüntüleyebilir."],
      ["duzeltme", "Hazır beton kayıtlarını düzeltebilir."],
      ["hakedis", "Hakediş sayfalarını kullanabilir."],
      ["yonetici", "Hazır beton modülünde yönetici yetkisi verir."],
    ],
  },
  {
    key: "filotakip",
    title: "Filo Takip",
    fields: [
      ["giris", "Filo modülünde veri girişi yapabilir."],
      ["takip", "Filo kayıtlarını görüntüleyebilir."],
      ["yonetici", "Filo modülünde yönetici işlemlerini yapabilir."],
    ],
  },
  {
    key: "kisiye-ozel-rapor",
    title: "Kişiye Özel Rapor",
    fields: [
      ["patron", "Patron raporlarını görüntüleyebilir."],
      ["aylik-isg", "Aylık İSG raporunu görüntüleyebilir."],
    ],
  },
];

const emptyPermissions = () =>
  MODULES.reduce((acc, module) => {
    acc[module.key] = {};
    module.fields.forEach(([field]) => {
      acc[module.key][field] = false;
    });
    if (module.plates) acc[module.key].allowedPlates = [];
    return acc;
  }, { global: { yetkiKontrol: false } });

const getModuleMap = (user, key) => {
  if (key === "arac-takip") return user["arac-takip"] || user.aractakip || {};
  return user[key] || {};
};

const getPersonAccess = (email, personList = []) => {
  const emailLc = String(email || "").toLocaleLowerCase("tr-TR");
  const match = personList.find((item) => String(item.email || "").toLocaleLowerCase("tr-TR") === emailLc);
  return {
    id: match?.id || "",
    personelTesisler: Array.isArray(match?.gorebilecegiTesisler) ? match.gorebilecegiTesisler : [],
    filoTesisler: Array.isArray(match?.gorebilecegiFiloTesisler) ? match.gorebilecegiFiloTesisler : [],
  };
};

const buildEditableState = (user) => {
  const next = emptyPermissions();
  next.global.yetkiKontrol = user.yetkiKontrol === true || user.YetkiKontrol === true;
  MODULES.forEach((module) => {
    const source = getModuleMap(user, module.key);
    module.fields.forEach(([field]) => {
      next[module.key][field] = source?.[field] === true;
    });
    if (module.plates) {
      next[module.key].allowedPlates = Array.isArray(source?.allowedPlates)
        ? source.allowedPlates
        : Array.isArray(source?.izinliPlakalar)
          ? source.izinliPlakalar
          : [];
    }
  });
  return next;
};

export default function UserPermissions() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, role, userPermissions, logout, loading } = useAuth();
  const [users, setUsers] = useState([]);
  const [plates, setPlates] = useState([]);
  const [persons, setPersons] = useState([]);
  const [personelTesisler, setPersonelTesisler] = useState([]);
  const [filoTesisler, setFiloTesisler] = useState([]);
  const [selectedEmail, setSelectedEmail] = useState("");
  const [editable, setEditable] = useState(emptyPermissions());
  const [linkedPersonId, setLinkedPersonId] = useState("");
  const [personelExtraTesisler, setPersonelExtraTesisler] = useState([]);
  const [filoExtraTesisler, setFiloExtraTesisler] = useState([]);
  const [saving, setSaving] = useState(false);
  const [filter, setFilter] = useState("");
  const [menuOpen, setMenuOpen] = useState(false);
  const [newUserEmail, setNewUserEmail] = useState("");
  const [newUserPassword, setNewUserPassword] = useState("");
  const [newUserRole, setNewUserRole] = useState("Yetkili");
  const [creatingUser, setCreatingUser] = useState(false);

  useEffect(() => {
    const handler = (e) => {
      if (!e.target.closest(".navbar")) setMenuOpen(false);
    };
    document.addEventListener("click", handler);
    return () => document.removeEventListener("click", handler);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location]);

  useEffect(() => {
    if (loading || !canManagePermissions(role, userPermissions)) return;
    (async () => {
      const [userSnap, plateSnap, personSnap] = await Promise.all([
        getDocs(collection(db, "users")),
        getDocs(collection(db, "plates")),
        getDocs(collection(db, "personeller")),
      ]);
      const loadedUsers = userSnap.docs
        .map((d) => ({ id: d.id, ...d.data(), email: d.data().email || d.id }))
        .sort((a, b) => String(a.email || "").localeCompare(String(b.email || ""), "tr"));
      const activePlates = plateSnap.docs
        .map((d) => ({ id: d.id, ...(d.data() || {}) }))
        .filter((item) => item.plate && item.active === true)
        .sort((a, b) => String(a.plate).localeCompare(String(b.plate), "tr"));
      const loadedPersons = personSnap.docs
        .map((d) => ({ id: d.id, ...(d.data() || {}) }))
        .sort((a, b) =>
          `${a.ad || ""} ${a.soyad || ""}`.localeCompare(`${b.ad || ""} ${b.soyad || ""}`, "tr")
        );
      const personTesisSet = new Set();
      loadedPersons.forEach((p) => {
        if (p.tesis) personTesisSet.add(String(p.tesis).trim());
      });
      const filoTesisSet = new Set();
      activePlates.forEach((p) => {
        if (p.tesis) filoTesisSet.add(String(p.tesis).trim());
      });
      setUsers(loadedUsers);
      setPlates(activePlates);
      setPersons(loadedPersons);
      setPersonelTesisler(Array.from(personTesisSet).sort((a, b) => a.localeCompare(b, "tr")));
      setFiloTesisler(Array.from(filoTesisSet).sort((a, b) => a.localeCompare(b, "tr")));
      if (loadedUsers[0]) {
        setSelectedEmail(loadedUsers[0].email);
        setEditable(buildEditableState(loadedUsers[0]));
        const access = getPersonAccess(loadedUsers[0].email, loadedPersons);
        setLinkedPersonId(access.id);
        setPersonelExtraTesisler(access.personelTesisler);
        setFiloExtraTesisler(access.filoTesisler);
      }
    })();
  }, [loading, role, userPermissions]);

  const selectedUser = useMemo(
    () => users.find((item) => item.email === selectedEmail) || null,
    [users, selectedEmail]
  );

  const filteredUsers = useMemo(() => {
    const needle = filter.trim().toLocaleLowerCase("tr-TR");
    if (!needle) return users;
    return users.filter((item) =>
      [item.email, item.role].some((value) => String(value || "").toLocaleLowerCase("tr-TR").includes(needle))
    );
  }, [users, filter]);

  const personOptions = useMemo(
    () =>
      persons
        .filter((item) => item.aktif !== false)
        .map((item) => ({
          id: item.id,
          label: `${item.ad || ""} ${item.soyad || ""}`.trim() || item.id,
          tesis: item.tesis || "",
          email: item.email || "",
        })),
    [persons]
  );

  const linkedPerson = useMemo(
    () => persons.find((item) => item.id === linkedPersonId) || null,
    [persons, linkedPersonId]
  );

  const selectUser = (email) => {
    const item = users.find((u) => u.email === email);
    setSelectedEmail(email);
    setEditable(buildEditableState(item || {}));
    const access = getPersonAccess(email, persons);
    setLinkedPersonId(access.id);
    setPersonelExtraTesisler(access.personelTesisler);
    setFiloExtraTesisler(access.filoTesisler);
  };

  const handleCreateUser = async (event) => {
    event.preventDefault();
    const email = newUserEmail.trim().toLocaleLowerCase("tr-TR");
    const password = newUserPassword.trim();
    const roleValue = newUserRole.trim() || "Yetkili";

    if (!email) return alert("Email girin.");
    if (!password || password.length < 6) return alert("Şifre en az 6 karakter olmalı.");
    if (users.some((item) => String(item.email || "").toLocaleLowerCase("tr-TR") === email)) {
      return alert("Bu email zaten kullanıcı listesinde var.");
    }

    setCreatingUser(true);
    try {
      const result = await createNewUser(email, password, roleValue);
      if (!result.success) return alert(`Kullanıcı oluşturulamadı: ${result.error}`);

      const created = { id: email, email, role: roleValue, active: true };
      setUsers((prev) => [...prev, created].sort((a, b) => String(a.email || "").localeCompare(String(b.email || ""), "tr")));
      setNewUserEmail("");
      setNewUserPassword("");
      setNewUserRole("Yetkili");
      setSelectedEmail(email);
      setEditable(buildEditableState(created));
      setLinkedPersonId("");
      setPersonelExtraTesisler([]);
      setFiloExtraTesisler([]);
      alert("Kullanıcı oluşturuldu. Şimdi yetkilerini düzenleyebilirsin.");
    } finally {
      setCreatingUser(false);
    }
  };

  const setPermission = (moduleKey, field, value) => {
    setEditable((prev) => ({
      ...prev,
      [moduleKey]: {
        ...prev[moduleKey],
        [field]: value,
      },
    }));
  };

  const togglePlate = (plate) => {
    setEditable((prev) => {
      const current = new Set(prev["kontrol-formlari"]?.allowedPlates || []);
      if (current.has(plate)) current.delete(plate);
      else current.add(plate);
      return {
        ...prev,
        "kontrol-formlari": {
          ...prev["kontrol-formlari"],
          allowedPlates: Array.from(current).sort((a, b) => a.localeCompare(b, "tr")),
        },
      };
    });
  };

  const clearPlateLimit = () => {
    setEditable((prev) => ({
      ...prev,
      "kontrol-formlari": {
        ...prev["kontrol-formlari"],
        allowedPlates: [],
      },
    }));
  };

  const toggleExtraTesis = (kind, tesis) => {
    const setter = kind === "personel" ? setPersonelExtraTesisler : setFiloExtraTesisler;
    setter((prev) => {
      const set = new Set(prev || []);
      if (set.has(tesis)) set.delete(tesis);
      else set.add(tesis);
      return Array.from(set).sort((a, b) => a.localeCompare(b, "tr"));
    });
  };

  const unlinkPerson = async () => {
    if (!linkedPersonId || !selectedEmail) return;
    if (!window.confirm("Bu kullanıcının personel-email eşleştirmesi kaldırılsın mı?")) return;
    await updateDoc(doc(db, "personeller", linkedPersonId), { email: "" });
    setPersons((prev) => prev.map((item) => item.id === linkedPersonId ? { ...item, email: "" } : item));
    setLinkedPersonId("");
    setPersonelExtraTesisler([]);
    setFiloExtraTesisler([]);
  };

  const save = async () => {
    if (!selectedUser?.email) return;
    setSaving(true);
    try {
      const payload = {};
      MODULES.forEach((module) => {
        payload[module.key] = {};
        module.fields.forEach(([field]) => {
          payload[module.key][field] = editable[module.key]?.[field] === true;
        });
        if (module.plates) {
          payload[module.key].allowedPlates = editable[module.key]?.allowedPlates || [];
        }
      });
      payload.aractakip = payload["arac-takip"];
      payload.yetkiKontrol = editable.global?.yetkiKontrol === true;
      payload.YetkiKontrol = editable.global?.yetkiKontrol === true;
      await setDoc(doc(db, "users", selectedUser.email), payload, { merge: true });

      if (linkedPersonId) {
        const emailLc = String(selectedUser.email || "").toLocaleLowerCase("tr-TR");
        const previousMatches = persons.filter(
          (item) =>
            item.id !== linkedPersonId &&
            String(item.email || "").toLocaleLowerCase("tr-TR") === emailLc
        );
        await Promise.all(previousMatches.map((item) => updateDoc(doc(db, "personeller", item.id), { email: "" })));
        await updateDoc(doc(db, "personeller", linkedPersonId), {
          email: selectedUser.email,
          gorebilecegiTesisler: personelExtraTesisler,
          gorebilecegiFiloTesisler: filoExtraTesisler,
        });
        setPersons((prev) =>
          prev.map((item) => {
            if (item.id === linkedPersonId) {
              return {
                ...item,
                email: selectedUser.email,
                gorebilecegiTesisler: personelExtraTesisler,
                gorebilecegiFiloTesisler: filoExtraTesisler,
              };
            }
            if (previousMatches.some((match) => match.id === item.id)) return { ...item, email: "" };
            return item;
          })
        );
      }
      alert("Yetkiler kaydedildi.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div style={centerStyle}>Yükleniyor...</div>;

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const navbar = (
    <nav className="navbar">
      <div className="navbar-left">
        <img src="/logo.png" alt="logo" className="navbar-logo" />
        <span className="navbar-title">Yetki Yönetimi</span>
      </div>

      {user && (
        <>
          <div className="hamburger" onClick={() => setMenuOpen(!menuOpen)}>
            <span></span><span></span><span></span>
          </div>
          <div className={`navbar-links ${menuOpen ? "show" : ""}`}>
            <NavLink className="navbar-link" to="/secilen-proje">Proje Seç</NavLink>
            <button type="button" className="logout-button" onClick={handleLogout}>Çıkış</button>
          </div>
        </>
      )}
    </nav>
  );

  if (!canManagePermissions(role, userPermissions)) {
    return (
      <>
        {navbar}
        <div style={centerStyle}>
          <div>Bu sayfayı görüntüleme yetkiniz yok.</div>
          <button type="button" onClick={() => navigate("/secilen-proje")} style={secondaryBtn}>Proje Seç</button>
        </div>
      </>
    );
  }

  return (
    <>
      {navbar}
      <div style={pageStyle}>
      <section style={headerStyle}>
        <div>
          <h1 style={{ margin: 0, fontSize: 24 }}>Yetki Yönetimi</h1>
          <div style={mutedStyle}>Kullanıcıların modül yetkilerini ve Kontrol Formları plaka kısıtlarını yönetin.</div>
        </div>
        <button type="button" onClick={() => navigate("/secilen-proje")} style={secondaryBtn}>Proje Seç</button>
      </section>

      <div style={layoutStyle}>
        <aside style={panelStyle}>
          <form onSubmit={handleCreateUser} style={createUserBoxStyle}>
            <h2 style={titleStyle}>Yeni Kullanıcı</h2>
            <input
              type="email"
              value={newUserEmail}
              onChange={(e) => setNewUserEmail(e.target.value)}
              placeholder="email@firma.com"
              style={inputStyle}
            />
            <input
              type="password"
              value={newUserPassword}
              onChange={(e) => setNewUserPassword(e.target.value)}
              placeholder="Geçici şifre"
              style={inputStyle}
            />
            <select value={newUserRole} onChange={(e) => setNewUserRole(e.target.value)} style={inputStyle}>
              <option value="Yetkili">Yetkili</option>
              <option value="Oluşturucu">Oluşturucu</option>
              <option value="Kullanıcı">Kullanıcı</option>
            </select>
            <button type="submit" disabled={creatingUser} style={primaryBtn}>
              {creatingUser ? "Oluşturuluyor..." : "Kullanıcı Oluştur"}
            </button>
          </form>

          <h2 style={titleStyle}>Kullanıcılar</h2>
          <input value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Kullanıcı ara" style={inputStyle} />
          <div style={userListStyle}>
            {filteredUsers.map((item) => (
              <button
                key={item.email}
                type="button"
                onClick={() => selectUser(item.email)}
                style={item.email === selectedEmail ? activeUserBtn : userBtn}
              >
                <b>{item.email}</b>
                <small>{item.role || "-"}</small>
              </button>
            ))}
          </div>
        </aside>

        <main style={panelStyle}>
          <div style={formHeaderStyle}>
            <div>
              <h2 style={titleStyle}>{selectedUser?.email || "Kullanıcı seçin"}</h2>
              <div style={mutedStyle}>Rol: {selectedUser?.role || "-"}</div>
            </div>
            <button type="button" onClick={save} disabled={saving || !selectedUser} style={primaryBtn}>
              {saving ? "Kaydediliyor..." : "Kaydet"}
            </button>
          </div>

          <section style={moduleStyle}>
            <h3 style={moduleTitleStyle}>Global Yetkiler</h3>
            <label style={fieldStyle}>
              <input
                type="checkbox"
                checked={editable.global?.yetkiKontrol === true}
                onChange={(e) =>
                  setEditable((prev) => ({
                    ...prev,
                    global: { ...prev.global, yetkiKontrol: e.target.checked },
                  }))
                }
              />
              <span>
                <b>yetkiKontrol</b>
                <small>Oluşturucu olmayan kullanıcının Yetki Yönetimi sayfasına girebilmesini sağlar.</small>
              </span>
            </label>
          </section>

          <section style={moduleStyle}>
            <h3 style={moduleTitleStyle}>Personel / Tesis Erişimi</h3>
            <div style={mutedStyle}>
              Personel ve filo modüllerinde kullanıcılar normalde eşleştiği personelin bağlı olduğu tesisi görür.
              Buradan kullanıcı e-postasını personele bağlayabilir ve ek görebileceği tesisleri verebilirsin.
            </div>

            <div style={accessGridStyle}>
              <label>
                Personel Eşleştirme
                <select
                  value={linkedPersonId}
                  onChange={(e) => {
                    const person = persons.find((item) => item.id === e.target.value);
                    setLinkedPersonId(e.target.value);
                    setPersonelExtraTesisler(Array.isArray(person?.gorebilecegiTesisler) ? person.gorebilecegiTesisler : []);
                    setFiloExtraTesisler(Array.isArray(person?.gorebilecegiFiloTesisler) ? person.gorebilecegiFiloTesisler : []);
                  }}
                  style={inputStyle}
                >
                  <option value="">Personel seçilmedi</option>
                  {personOptions.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.label} {item.tesis ? `- ${item.tesis}` : ""} {item.email ? `(${item.email})` : ""}
                    </option>
                  ))}
                </select>
              </label>

              <div>
                <b>Bağlı Tesis</b>
                <div style={readonlyBoxStyle}>{linkedPerson?.tesis || "-"}</div>
              </div>

              <div>
                <b>Eşleşecek Email</b>
                <div style={readonlyBoxStyle}>{selectedUser?.email || "-"}</div>
              </div>

              <div style={{ alignSelf: "end" }}>
                <button type="button" onClick={unlinkPerson} disabled={!linkedPersonId} style={dangerBtn}>
                  Eşleştirmeyi Kaldır
                </button>
              </div>
            </div>

            <div style={tesisColumnsStyle}>
              <div style={tesisBoxStyle}>
                <b>Personel Modülünde Ek Görebileceği Tesisler</b>
                <div style={mutedStyle}>Alan adı: personeller.gorebilecegiTesisler</div>
                <div style={tesisGridStyle}>
                  {personelTesisler.map((tesis) => (
                    <label key={tesis} style={plateItemStyle}>
                      <input
                        type="checkbox"
                        disabled={!linkedPersonId}
                        checked={personelExtraTesisler.includes(tesis)}
                        onChange={() => toggleExtraTesis("personel", tesis)}
                      />
                      <span>{tesis}</span>
                    </label>
                  ))}
                  {!personelTesisler.length && <div style={mutedStyle}>Tesis bulunamadı.</div>}
                </div>
              </div>

              <div style={tesisBoxStyle}>
                <b>Filo/Araç Modülünde Ek Görebileceği Tesisler</b>
                <div style={mutedStyle}>Alan adı: personeller.gorebilecegiFiloTesisler</div>
                <div style={tesisGridStyle}>
                  {filoTesisler.map((tesis) => (
                    <label key={tesis} style={plateItemStyle}>
                      <input
                        type="checkbox"
                        disabled={!linkedPersonId}
                        checked={filoExtraTesisler.includes(tesis)}
                        onChange={() => toggleExtraTesis("filo", tesis)}
                      />
                      <span>{tesis}</span>
                    </label>
                  ))}
                  {!filoTesisler.length && <div style={mutedStyle}>Tesis bulunamadı.</div>}
                </div>
              </div>
            </div>
          </section>

          <div style={modulesStyle}>
            {MODULES.map((module) => (
              <section key={module.key} style={moduleStyle}>
                <h3 style={moduleTitleStyle}>{module.title}</h3>
                <div style={fieldsStyle}>
                  {module.fields.map(([field, description]) => (
                    <label key={field} style={fieldStyle}>
                      <input
                        type="checkbox"
                        checked={editable[module.key]?.[field] === true}
                        onChange={(e) => setPermission(module.key, field, e.target.checked)}
                      />
                      <span>
                        <b>{field}</b>
                        <small>{description}</small>
                      </span>
                    </label>
                  ))}
                </div>

                {module.plates && (
                  <div style={plateBoxStyle}>
                    <div style={{ display: "flex", justifyContent: "space-between", gap: 8, alignItems: "center" }}>
                      <div>
                        <b>Görebileceği Plakalar</b>
                        <div style={mutedStyle}>Boş bırakılırsa tüm aktif araçları doldurabilir.</div>
                      </div>
                      <button type="button" onClick={clearPlateLimit} style={secondaryBtn}>Tüm Aktifler</button>
                    </div>
                    <div style={platesGridStyle}>
                      {plates.map((item) => (
                        <label key={item.id} style={plateItemStyle}>
                          <input
                            type="checkbox"
                            checked={(editable["kontrol-formlari"]?.allowedPlates || []).includes(item.plate)}
                            onChange={() => togglePlate(item.plate)}
                          />
                          <span>{item.plate}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}
              </section>
            ))}
          </div>
        </main>
      </div>
      </div>
    </>
  );
}

const pageStyle = { minHeight: "100vh", background: "#f8fafc", padding: 18 };
const headerStyle = { maxWidth: 1300, margin: "0 auto 14px", display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center" };
const layoutStyle = { maxWidth: 1300, margin: "0 auto", display: "grid", gridTemplateColumns: "300px 1fr", gap: 14, alignItems: "start" };
const panelStyle = { background: "#fff", border: "1px solid #e2e8f0", borderRadius: 8, padding: 14 };
const titleStyle = { margin: "0 0 10px", fontSize: 18 };
const mutedStyle = { color: "#64748b", fontSize: 13 };
const inputStyle = { width: "100%", boxSizing: "border-box", padding: 9, border: "1px solid #cbd5e1", borderRadius: 6 };
const createUserBoxStyle = { display: "grid", gap: 8, borderBottom: "1px solid #e2e8f0", paddingBottom: 14, marginBottom: 14 };
const userListStyle = { display: "grid", gap: 7, marginTop: 10, maxHeight: "70vh", overflow: "auto" };
const userBtn = { textAlign: "left", background: "#fff", border: "1px solid #e2e8f0", borderRadius: 6, padding: 9, cursor: "pointer", display: "grid", gap: 3 };
const activeUserBtn = { ...userBtn, borderColor: "#2563eb", background: "#eff6ff" };
const formHeaderStyle = { display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center", marginBottom: 12 };
const modulesStyle = { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))", gap: 12 };
const moduleStyle = { border: "1px solid #e2e8f0", borderRadius: 8, padding: 12 };
const moduleTitleStyle = { margin: "0 0 10px", fontSize: 16 };
const fieldsStyle = { display: "grid", gap: 8 };
const fieldStyle = { display: "grid", gridTemplateColumns: "20px 1fr", gap: 8, alignItems: "start" };
const plateBoxStyle = { borderTop: "1px solid #e2e8f0", marginTop: 12, paddingTop: 12 };
const platesGridStyle = { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))", gap: 7, marginTop: 10, maxHeight: 220, overflow: "auto" };
const plateItemStyle = { display: "flex", gap: 6, alignItems: "center", border: "1px solid #e2e8f0", borderRadius: 6, padding: 7 };
const accessGridStyle = { display: "grid", gridTemplateColumns: "minmax(260px, 1.3fr) 1fr 1fr auto", gap: 10, alignItems: "center", marginTop: 12 };
const readonlyBoxStyle = { marginTop: 6, minHeight: 37, display: "flex", alignItems: "center", border: "1px solid #e2e8f0", borderRadius: 6, padding: "8px 10px", background: "#f8fafc" };
const tesisColumnsStyle = { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 12, marginTop: 12 };
const tesisBoxStyle = { border: "1px solid #e2e8f0", borderRadius: 8, padding: 10 };
const tesisGridStyle = { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(135px, 1fr))", gap: 7, marginTop: 10, maxHeight: 220, overflow: "auto" };
const primaryBtn = { background: "#2563eb", color: "#fff", border: "none", borderRadius: 6, padding: "9px 13px", cursor: "pointer" };
const secondaryBtn = { background: "#f8fafc", color: "#0f172a", border: "1px solid #cbd5e1", borderRadius: 6, padding: "8px 10px", cursor: "pointer" };
const dangerBtn = { background: "#fee2e2", color: "#991b1b", border: "1px solid #fecaca", borderRadius: 6, padding: "8px 10px", cursor: "pointer" };
const centerStyle = { minHeight: "100vh", display: "grid", placeItems: "center", background: "#f8fafc", gap: 10 };
